import emailjs from '@emailjs/browser';

/**
 * EmailJS credentials (replace with your actual values)
 */
const EMAILJS_SERVICE_ID = 'service_5dwwuv7';
const EMAILJS_TEMPLATE_ID = 'template_6doitao';
// Optional: separate template for generic broadcasts (configure in EmailJS)
// Using dedicated broadcast template created in EmailJS dashboard
const EMAILJS_BROADCAST_TEMPLATE_ID = 'template_5wp4yhq';
const EMAILJS_PUBLIC_KEY = 'VxuiSaOeUyLAd5qm7';

/**
 * Public header image bundled inside /public/event-logo.jpeg
 * Email clients need an absolute URL, so we leverage the build-time PUBLIC_URL.
 */
const EVENT_HEADER_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/event-logo.jpeg`;

const DEFAULT_EVENT_DETAILS = {
  eventName: 'Old Skool Hangout',
  eventDate: 'December 15, 2025',
  eventTime: '6:00 PM - 11:00 PM',
  eventLocation: 'Kanu Ndubisi park, Mobolaji Johnson Ikeja'
};

let emailJsInitialized = false;

const ensureEmailJsInitialized = () => {
  if (!emailJsInitialized) {
    emailjs.init(EMAILJS_PUBLIC_KEY);
    emailJsInitialized = true;
  }
};

/**
 * Send ticket email to user
 * @param {Object} ticketData - Ticket data including ID, QR code, user email, etc.
 * @returns {Promise<boolean>} Success status
 */
export const sendTicketEmail = async (ticketData) => {
  try {
    ensureEmailJsInitialized();

    // Generate email HTML
    const emailHTML = generateTicketEmailHTML(ticketData);

    const {
      eventName = DEFAULT_EVENT_DETAILS.eventName,
      eventDate = DEFAULT_EVENT_DETAILS.eventDate,
      eventTime = DEFAULT_EVENT_DETAILS.eventTime,
      eventLocation = DEFAULT_EVENT_DETAILS.eventLocation
    } = ticketData || {};

    // Prepare email template parameters
    const templateParams = {
      to_email: ticketData.userEmail,
      to_name: ticketData.userName,
      ticket_id: ticketData.ticketId,
      event_name: eventName,
      event_date: eventDate,
      event_time: eventTime,
      event_location: eventLocation,
      qr_code_url: ticketData.qrCodeDataUrl,
      message: emailHTML,
      ticket_image_url: ticketData.ticketImageDataUrl || ticketData.qrCodeDataUrl,
      from_name: 'MTU Alumni Association',
      from_email: 'mtualumni@mtu.edu.ng',
      reply_to: 'mtualumni@mtu.edu.ng'
    };

    // Send email using EmailJS
    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams);
    console.log('Ticket email sent successfully to:', ticketData.userEmail);
    return true;
  } catch (error) {
    console.error('Error sending ticket email:', error);
    // Don't throw error - allow ticket to be saved even if email fails
    return false;
  }
};

/**
 * Generate email HTML template for ticket
 * @param {Object} ticketData - Ticket data
 * @returns {string} HTML email template
 */
export const generateTicketEmailHTML = (ticketData) => {
  const {
    eventName = DEFAULT_EVENT_DETAILS.eventName,
    eventDate = DEFAULT_EVENT_DETAILS.eventDate,
    eventTime = DEFAULT_EVENT_DETAILS.eventTime,
    eventLocation = DEFAULT_EVENT_DETAILS.eventLocation
  } = ticketData || {};

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Your Event Ticket</title>
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="border-radius: 10px 10px 0 0; overflow: hidden;">
        <img src="${ticketData.headerImageUrl || EVENT_HEADER_IMAGE_URL}" alt="Old Skool Hangout Banner" style="display: block; width: 100%; height: auto;">
      </div>
      
      <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px;">
        <h2 style="color: #333; margin-top: 0;">Hello ${ticketData.userName}!</h2>
        
        <p>Your ticket for the MTU Alumni Association event has been confirmed!</p>
        
        <div style="background: white; padding: 20px; border-radius: 8px; margin: 20px 0; border: 2px solid #667eea;">
          <p style="margin: 0 0 10px 0;"><strong>Ticket ID:</strong> ${ticketData.ticketId}</p>
          <p style="margin: 0 0 10px 0;"><strong>Event:</strong> ${eventName}</p>
          <p style="margin: 0 0 10px 0;"><strong>Date:</strong> ${eventDate}</p>
          <p style="margin: 0 0 10px 0;"><strong>Time:</strong> ${eventTime}</p>
          <p style="margin: 0;"><strong>Location:</strong> ${eventLocation}</p>
        </div>
        
        <div style="text-align: center; margin: 30px 0;">
          <img src="${ticketData.qrCodeDataUrl}" alt="QR Code" style="max-width: 300px; border: 2px solid #ddd; border-radius: 8px; padding: 10px; background: white;">
          <p style="font-size: 12px; color: #666; margin-top: 10px;">Please present this QR code at the event entrance</p>
        </div>
        
        <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <p style="margin: 0;"><strong>Important:</strong> Please keep this email safe. You'll need to show this QR code at the event entrance.</p>
        </div>
        
        <p style="margin-top: 30px;">We look forward to seeing you at the event!</p>
        
        <p style="margin-top: 20px; color: #666; font-size: 14px;">
          Best regards,<br>
          MTU Alumni Association
        </p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Send a generic email broadcast to a single recipient.
 * This uses a separate EmailJS template so you can design a different layout for broadcasts.
 *
 * Required template variables (in EmailJS):
 * - to_email
 * - to_name
 * - subject
 * - message_body
 * - from_name
 * - from_email
 */
export const sendBroadcastEmail = async ({ toEmail, toName, subject, messageBody }) => {
  try {
    ensureEmailJsInitialized();

    const templateParams = {
      to_email: toEmail,
      to_name: toName,
      subject,
      message_body: messageBody,
      from_name: 'MTU Alumni Association',
      from_email: 'mtualumni@mtu.edu.ng',
      reply_to: 'mtualumni@mtu.edu.ng'
    };

    await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_BROADCAST_TEMPLATE_ID, templateParams);
    console.log('Broadcast email sent to:', toEmail);
    return true;
  } catch (error) {
    console.error('Error sending broadcast email to', toEmail, error);
    return false;
  }
};

